/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNumber from 'lodash.isnumber';
import isUndefined from 'lodash.isundefined';
import {parseExtendedAttributes} from '@ion/core/src/services/ConnectUtils';
import {translate} from '../locale/locale';
import {
  GroupFormFields
} from '../types/editors';
import {
  Account,
  CTCEntityResponseWrapper,
  Entity
} from '../types/entities';
import {
  BulkOperationResults,
  ExtendedResults
} from '../types/services';
import {
  EntityTemplate,
  TemplateColumn,
  TemplateOperators,
  TemplateValidation
} from '../types/templates';
import {Mapped} from '../utils/ObjectUtil';
import {
  createLookupSheet,
  createValidation,
  formatErrors,
  interpolateValidation,
  localizeFormulae,
  statusColumn
} from './TemplateHelpers';

const localizedColumn = (localeKey: string) => ({validation}: {
  validation: TemplateValidation
}): TemplateValidation => {
  const value         = localizeFormulae(localeKey);
  validation.error    = interpolateValidation(validation, value);
  validation.formulae = [`"${value}"`];
  return validation;
};

const timeZoneColumn = ({validation, workbook}): TemplateValidation => {
  const timezoneOptions  = translate('timezones:list', {returnObjects: true});
  const values: string[] = [];
  timezoneOptions.forEach(({label}) => values.push(label.replaceAll(',', ' •')));
  validation.formulae = createLookupSheet({
    sheetName    : 'TimeZones',
    optionString : values.toString(),
    hide         : true,
    workbook
  });
  return validation;
};

const unitsColumn      = localizedColumn('admin-groups:units');
const autoAssetColumn  = localizedColumn('common:booleans');
const timeFormatColumn = localizedColumn('admin-groups:timeFormats');
const dateFormatColumn = localizedColumn('admin-groups:dateFormats');

/**
 * Provides a utility function which manages the dynamic generation of an XLSX
 * spreadsheet specific to Account / Group imports for download by the User.
 */
export const groupsTemplate = ({columns, workbook, options = {}}: EntityTemplate) => {
  const columnEntries: TemplateColumn[]     = [];
  const columnValidations: TemplateValidation[] = [];
  const templateColumns = {
    [GroupFormFields.Status]               : statusColumn,
    [GroupFormFields.UnitOfMeasure]        : unitsColumn,
    [GroupFormFields.DateFormat]           : dateFormatColumn,
    [GroupFormFields.TimeFormat]           : timeFormatColumn,
    [GroupFormFields.timeZone]             : timeZoneColumn,
    [GroupFormFields.AutoAssetAssociation] : autoAssetColumn,
    [GroupFormFields.AutoAssetCreation]    : autoAssetColumn
  };
  columns.forEach((column, index) => {
    const {key, type, required, max, min, error, width, style} = column;
    const validation = createValidation({required, error, index, type});
    const {header} = column;
    if (error) {
      if (isNumber(max) && required) {
        validation.formulae = [1, max];
        validation.operator = TemplateOperators.Between;
      } else if (isNumber(max)) {
        validation.formulae = [max];
        validation.operator = TemplateOperators.LessThanOrEqual;
      } else if (isNumber(min)) {
        validation.formulae = [min];
        validation.operator = TemplateOperators.GreaterThanOrEqual;
      }
    }
    columnEntries.push({key, header, width, style});
    columnValidations.push(templateColumns[key]?.({column, workbook, validation, options}) || validation);
  });
  return {
    columnEntries,
    columnValidations
  };
};

/**
 * Provides a utility function which manages the generation of spreadsheet errors
 * specific to Groups import for download by the user.
 *
 * ```typescript
 *
 * import {getGroupsImportErrors} from '@ion/core/src/xlsx/GroupsTemplate';
 *
 * const processedResult = getGroupsImportErrors(invalidGroups, result);
 * ```
 *
 * @param invalidGroups Groups which are invalid to be imported.
 * @param result Response returned from server.
 * @returns The processed results with errors if any
 */
export function getGroupsImportErrors(
  invalidGroups: CTCEntityResponseWrapper<Account>[],
  result: ExtendedResults
): ExtendedResults {
  if (result?.errors?.length) {
    const formattedErrors = formatErrors(result?.errors, Entity.account);
    result.errors = [];
    result.errors = [...formattedErrors];
  }
  if (invalidGroups?.length) {
    invalidGroups.forEach(({account}: any) => {
      let message;
      const groupName = account?.title ?? account.name;
      if (account.parentGroup) {
        message = translate('admin-groups:import.errors.invalidGroup', {
          account : groupName,
          parent  : account?.name
        });
      } else if (!account) {
        message = translate('admin-groups:import.errors.noGroup', {
          account : groupName
        });
      }
      result?.errors?.push({
        error : message,
        name  : groupName,
        message,
        account
      });
    });
  }
  return result;
};

/**
 * Provides a utility function which maps the results against the template columns
 * before the same is available as excel for user to download.
 *
 * ```typescript
 *
 * import {exportMapper} from '@ion/core/src/xlsx/GroupsTemplate';
 *
 * const processedResult = exportMapper(invalidGroups, result);
 * ```
 *
 * @param results Response from server.
 * @param boolean Checks if response has errors only.
 * @param Mapped Map which helps in mapping the results against respective
 * template columns.
 * @returns The results mapped against the respective columns for generating
 * excel data.
 */
export function exportMapper(
  {results, errors}: BulkOperationResults | ExtendedResults,
  errorsOnly: boolean,
  attributes: Mapped
): unknown[] {
  const entries         = [];
  const items           = errorsOnly ? errors : results?.concat(errors);
  const succeeded       = translate('common:succeeded');
  const failed          = translate('common:failed');
  const unavailable     = translate('common:notAvailable');
  const timezoneOptions   = translate('timezones:list', {returnObjects: true});
  const timezones       = {};
  timezoneOptions.forEach(({label, value}) => {
    timezones[label.toLowerCase()] = value;
  });
  const formatter       = (categories: any) => categories?.toString() ?? '';
  items.forEach(item => {
    const entry         = [];
    const isGlobalError = !isUndefined(item.code);
    const hasErrors     = !isUndefined(item.error) || isGlobalError;
    const result        = hasErrors ? item.account : item;
    Object.values(attributes)?.forEach((attribute: string) => {
      if (!isGlobalError || item.account) {
        const categories = parseExtendedAttributes(item, true) || {};
        const val = result[attribute];
        switch (attribute) {
          case 'parentGroup':
            entry.push(item?.parent?.title ?? '');
            break;
          case 'landmarkCategories':
          case 'routeCategories':
          case 'operatorCategories':
          case 'assetCategories':
          case 'dateFormat':
          case 'timeFormat':
            entry.push(formatter(categories?.[attribute]));
            break;
          default :
            entry.push(val);
            break;
        }
      } else {
        entry.push(unavailable);
      }
    });
    entry.push(hasErrors ? failed : succeeded);
    entry.push(hasErrors ? (!isGlobalError ? item.error : item.message) : '');
    entries.push(entry);
  });
  return entries;
};
